import { Heart, MessageSquare, Newspaper, Scale, Star, Users } from "lucide-react";
import type { MentionSummary } from "@/shared/api/api-client";
import { IfCena } from "@/shared/components/cena-visibility";
import { formatCurrencyCompact } from "@/shared/utils/format-currency";


import { formatCena, formatInteger, formatSignedCompact } from "@/shared/utils/format";
type Props = {
  summary: MentionSummary;
  /**
   * A Home mostra o resumo de UMA empresa (a última visitada) e não tem
   * `CompanyContext`, então o `IfCena` dela cairia no default "exibe".
   * Quem chama de lá passa a flag da empresa do resumo.
   */
  showsCena?: boolean;
};

export function DashboardSummaryCards({ summary, showsCena }: Props) {
  const cenaCard = (
    <article className="dashboard-summary-card dashboard-summary-card--dark">
      <Star className="dashboard-summary-watermark" aria-hidden="true"/>
      <div className="dashboard-summary-card-head">
        <div>
          <span className="dashboard-summary-label">Nota CENA</span>
          {/* O `icena` só existe pra imprensa nas tabelas vivas — post de
              rede só ganha nota dentro de amostra. Numa empresa só de
              rede social o valor é `—` pra sempre, e o sublabel é o que
              impede a pessoa de ler isso como sistema quebrado. */}
          <span className="dashboard-summary-sublabel">
            {summary.press_total_mentions > 0
              ? "Média das menções de imprensa (0-10)"
              : "Sem menções de imprensa na janela"}
          </span>
        </div>
        <span className="dashboard-summary-icon"><Star size={17}/></span>
      </div>
      <strong className="dashboard-summary-value">{formatCena(summary.average_cena)}</strong>
    </article>
  );

  const balanceCard = (
    <article className="dashboard-summary-card dashboard-summary-card--dark">
      <Scale className="dashboard-summary-watermark" aria-hidden="true"/>
      <div className="dashboard-summary-card-head">
        <div>
          <span className="dashboard-summary-label">Saldo reputacional</span>
          <span className="dashboard-summary-sublabel">
            {summary.scored_mentions > 0
              ? `Soma do ESCena de ${formatInteger(summary.scored_mentions)} menções com nota`
              : "Nenhuma menção com nota na janela"}
          </span>
        </div>
        <span className="dashboard-summary-icon"><Scale size={17}/></span>
      </div>
      <strong
        className="dashboard-summary-value"
        title={summary.cena_balance == null ? undefined : formatInteger(summary.cena_balance)}
      >
        {formatSignedCompact(summary.cena_balance)}
      </strong>
    </article>
  );

  return (
    <>
      <div className="dashboard-summary-grid">
        <article className="dashboard-summary-card dashboard-summary-card--card1">
          <MessageSquare className="dashboard-summary-watermark" aria-hidden="true"/>
          <div className="dashboard-summary-card-head">
            <div>
              <span className="dashboard-summary-label">Volume total</span>
              <span className="dashboard-summary-sublabel">Menções monitoradas</span>
            </div>
            <span className="dashboard-summary-icon"><MessageSquare size={17}/></span>
          </div>
          <strong className="dashboard-summary-value">{formatInteger(summary.total_mentions)}</strong>
        </article>

        <article className="dashboard-summary-card dashboard-summary-card--card2">
          <Users className="dashboard-summary-watermark" aria-hidden="true"/>
          <div className="dashboard-summary-card-head">
            <div>
              <span className="dashboard-summary-label">Alcance potencial</span>
              <span className="dashboard-summary-sublabel">20% dos seguidores, cada perfil uma vez</span>
            </div>
            <span className="dashboard-summary-icon"><Users size={17}/></span>
          </div>
          <strong className="dashboard-summary-value">{formatInteger(summary.total_reach)}</strong>
        </article>

        <article className="dashboard-summary-card dashboard-summary-card--card3">
          <Heart className="dashboard-summary-watermark" aria-hidden="true"/>
          <div className="dashboard-summary-card-head">
            <div>
              <span className="dashboard-summary-label">Volume de interações</span>
              <span className="dashboard-summary-sublabel">Engajamentos gerados</span>
            </div>
            <span className="dashboard-summary-icon"><Heart size={17}/></span>
          </div>
          <strong className="dashboard-summary-value">{formatInteger(summary.total_interactions)}</strong>
        </article>

        {showsCena === undefined ? <IfCena>{cenaCard}</IfCena> : showsCena ? cenaCard : null}
        {showsCena === undefined ? <IfCena>{balanceCard}</IfCena> : showsCena ? balanceCard : null}
      </div>

      <div className="dashboard-press-panel">
        <div className="dashboard-press-header">
          <div className="dashboard-press-title">
            <Newspaper size={16}/>
            <span>Visão geral — Imprensa</span>
          </div>
        </div>
        <div className="dashboard-press-grid">
          <div>
            <span className="dashboard-press-metric-label">Volume total</span>
            <strong className="dashboard-press-metric-value">{formatInteger(summary.press_total_mentions)}</strong>
            <span className="dashboard-press-metric-sublabel">Menções em imprensa</span>
          </div>
          <div>
            <span className="dashboard-press-metric-label">Total de veículos</span>
            <strong className="dashboard-press-metric-value">{formatInteger(summary.press_distinct_outlets)}</strong>
            <span className="dashboard-press-metric-sublabel">Veículos distintos</span>
          </div>
          <div>
            <span className="dashboard-press-metric-label">Favoráveis</span>
            <strong className="dashboard-press-metric-value">{formatInteger(summary.positive_count)}</strong>
            <span className="dashboard-press-metric-sublabel">De {formatInteger(summary.total_mentions)} menções</span>
          </div>
          <div>
            <span className="dashboard-press-metric-label">Desfavoráveis</span>
            <strong className="dashboard-press-metric-value">{formatInteger(summary.negative_count)}</strong>
            <span className="dashboard-press-metric-sublabel">Pedem atenção</span>
          </div>
          <div>
            {/* Valoração é fato de imprensa, então este painel é o
                lugar natural dela. `numberFormatter` NÃO serve aqui:
                ele estoura com `null`, e null é o caso comum (janela
                sem matéria valorada, ou uploads anteriores à
                ingestão). */}
            <span className="dashboard-press-metric-label">Valoração</span>
            <strong className="dashboard-press-metric-value">
              {formatCurrencyCompact(summary.press_total_ad_value)}
            </strong>
            <span className="dashboard-press-metric-sublabel">
              {summary.press_total_ad_value == null
                ? "Nenhuma matéria com valoração na janela"
                : "Valor publicitário estimado"}
            </span>
          </div>
          <div>
            {/* Alcance de IMPRENSA, à parte do de rede (nunca se somam):
                audiência que a planilha da clipadora informa, cada veículo
                uma vez na janela. `null` = nenhuma matéria trouxe alcance,
                e isso não é "0". */}
            <span className="dashboard-press-metric-label">Alcance imprensa</span>
            <strong className="dashboard-press-metric-value">
              {summary.press_reach == null ? "—" : formatInteger(summary.press_reach)}
            </strong>
            <span className="dashboard-press-metric-sublabel">
              {summary.press_reach == null
                ? "Nenhuma matéria com alcance na janela"
                : "Uma vez por veículo, valor da planilha"}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
